/* AI Memory Moments — choose a style + a source photo, confirm consent,
   click Generate. Result saves to the memorial and is shown below as a
   gallery. Faces are preserved from the source photograph by the model
   prompt; we never invent faces. */
import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, Check, AlertCircle, Trash2 } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { toast } from 'sonner';

const ICONS = {
  religious: '✦',
  happy_moments: '☀',
  hug_embrace: '♡',
  walking_together: '∼',
  family_gathering: '⌂',
  gentle_tribute: '◌',
};

export function AIMemoryMoments({ memorialId }) {
  const [styles, setStyles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [moments, setMoments] = useState([]);
  const [styleId, setStyleId] = useState(null);
  const [sourceId, setSourceId] = useState(null);
  const [consent, setConsent] = useState(false);
  const [note, setNote] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    const [s, p, m] = await Promise.all([
      api.get('/ai/memory-styles'),
      api.get(`/memorials/${memorialId}/media`),
      api.get(`/memorials/${memorialId}/ai/memory-moments`),
    ]);
    setStyles(s.data.styles || []);
    // The media endpoint returns {media: [{media_id, kind, external_url, ...}]}.
    // We only offer photos as a source for AI memory moments.
    setPhotos((p.data.media || []).filter((x) => x.kind === 'image'));
    setMoments(m.data.moments || []);
  }, [memorialId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const generate = async () => {
    if (!styleId || !sourceId || !consent) return;
    setGenerating(true);
    setError(null);
    try {
      await api.post(`/memorials/${memorialId}/ai/memory-moments`, {
        style: styleId,
        source_photo_id: sourceId,
        consent_acknowledged: true,
        note,
      });
      toast.success('Memory moment created');
      setNote('');
      loadAll();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (mid) => {
    if (!window.confirm('Delete this memory moment?')) return;
    await api.delete(`/memorials/${memorialId}/ai/memory-moments/${mid}`);
    loadAll();
  };

  return (
    <div className="ml-card" data-testid="ai-memory-moments">
      <div className="flex items-baseline gap-2 text-[hsl(var(--accent))]">
        <Sparkles className="w-3.5 h-3.5" />
        <div className="ml-overline">AI Memory Moments</div>
      </div>
      <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-2">
        Reimagine a photograph, gently.
      </h3>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed max-w-2xl">
        Pick one of their photos and a quiet style. We'll use Gemini Nano Banana
        to create a soft scene around their existing face — we never invent
        faces. The family must consent to each generation.
      </p>

      {/* 1. Style picker */}
      <div className="mt-7">
        <div className="ml-overline mb-3">1 · Choose a style</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ai-style-grid">
          {styles.map((s) => {
            const sel = styleId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setStyleId(s.id)}
                data-testid={`ai-style-${s.id}`}
                className={
                  'text-left rounded-lg border p-4 transition-all ' +
                  (sel
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5 shadow-sm'
                    : 'border-[hsl(var(--primary)/0.12)] hover:border-[hsl(var(--primary)/0.35)]')
                }
              >
                <div className="flex items-center gap-2">
                  <span
                    className="font-serif text-xl text-[hsl(var(--accent))] w-6 text-center"
                    aria-hidden
                  >
                    {ICONS[s.id] || '◌'}
                  </span>
                  <span className="font-serif text-lg text-[hsl(var(--primary))]">
                    {s.label}
                  </span>
                  {sel && <Check size={14} className="ml-auto text-[hsl(var(--accent))]" />}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
                  {s.description}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Photo picker */}
      <div className="mt-8">
        <div className="ml-overline mb-3">
          2 · Choose a photograph (their face will be preserved)
        </div>
        {photos.length === 0 ? (
          <div className="text-sm text-[hsl(var(--muted-foreground))] italic font-serif">
            Upload a photo on the Memories tab first — we use that as the source.
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="ai-photo-grid">
            {photos.slice(0, 30).map((p) => {
              const sel = sourceId === p.media_id;
              const src = p.external_url || (p.storage_path
                ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${p.media_id}/file`
                : '');
              return (
                <button
                  key={p.media_id}
                  type="button"
                  onClick={() => setSourceId(p.media_id)}
                  data-testid={`ai-photo-${p.media_id}`}
                  className={
                    'relative aspect-square rounded-md overflow-hidden border-2 transition-all ' +
                    (sel ? 'border-[hsl(var(--accent))]' : 'border-transparent hover:border-[hsl(var(--primary)/0.3)]')
                  }
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                  {sel && (
                    <div className="absolute inset-0 bg-[hsl(var(--accent))]/30 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" strokeWidth={3} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 3. Consent + note */}
      <div className="mt-8">
        <div className="ml-overline mb-3">3 · Family consent</div>
        <label className="flex items-start gap-3 cursor-pointer p-4 rounded-md border border-[hsl(var(--primary)/0.15)] bg-[hsl(var(--surface))]">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[hsl(var(--accent))]"
            data-testid="ai-consent-checkbox"
          />
          <span className="text-sm text-[hsl(var(--primary)/0.85)] leading-relaxed">
            I confirm the family <strong>consents</strong> to creating an AI memory
            image based on this photograph. I understand the resulting image is a
            gentle reimagining and not a real photograph of the deceased.
          </span>
        </label>
        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional: a private note about this moment (300 chars max)"
          maxLength={300}
          className="ml-input mt-3"
          data-testid="ai-note"
        />
      </div>

      {error && (
        <div
          className="mt-5 flex items-start gap-2 text-sm text-[hsl(var(--destructive))]"
          data-testid="ai-error"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="button"
        disabled={!styleId || !sourceId || !consent || generating}
        onClick={generate}
        className="ml-btn-gold mt-6"
        data-testid="ai-generate-btn"
      >
        <Sparkles className="w-4 h-4" />
        {generating ? 'Generating (this can take ~30s)…' : 'Generate memory moment'}
      </button>

      {/* Gallery of generated moments */}
      {moments.length > 0 && (
        <div className="mt-10 pt-8 border-t border-[hsl(var(--primary)/0.08)]">
          <div className="ml-overline mb-3">Memory moments ({moments.length})</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-moments-gallery">
            {moments.map((m) => (
              <div
                key={m.moment_id}
                className="rounded-md border border-[hsl(var(--primary)/0.1)] overflow-hidden bg-[hsl(var(--surface))]"
                data-testid={`ai-moment-${m.moment_id}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${m.image_url}`}
                  alt={m.style_label}
                  className="w-full aspect-square object-cover"
                />
                <div className="p-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--accent))]">
                      {m.style_label}
                    </div>
                    {m.note && (
                      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">
                        {m.note}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(m.moment_id)}
                    aria-label="Delete moment"
                    className="text-[hsl(var(--destructive))] hover:opacity-80"
                    data-testid={`ai-moment-delete-${m.moment_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
